import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Collapse, Grid, Link, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useExternalErrors } from '@alycecom/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { CommonData } from '@alycecom/modules';
import { LinkButton } from '@alycecom/ui';

import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import { addressValidationSchema, getAddressDefaultValues } from '../../../store/shipping/shipping.schemas';
import { IShippingAddress, IShippingFormFields, ShippingAddressField } from '../../../store/shipping/shipping.types';
import { getInitialShippingValues } from '../../../store/recipient.selectors';
import { getErrors, getIsManualAddressMode, getIsPending } from '../../../store/shipping/shipping.selectors';
import { getCountryId, getHasShippingAddress, getProduct } from '../../../store/giftFlow/giftFlow.selectors';
import { getCanBeExchanged } from '../../../store/data/data.selectors';
import { setManualAddressMode } from '../../../store/shipping/shipping.actions';
import { IVerifiedAddressOption } from '../../../types/Shipping.types';
import useTrackRecipient from '../../../../../hooks/useTrackRecipient';
import { isAddressFieldOptional } from '../../../../../store/countries/countries.helpers';
import { getSenderState } from '../../../../../store/users/users.selectors';
import { CountryAddressField } from '../../../../../store/countries/countries.types';
import { getCountryById } from '../../../../../store/countries/countries.selectors';
import { getBranding } from '../../../../../store/branding/branding.selectors';
import { BrandingFields } from '../../../../../types/Branding.types';

import InfoTooltip from './InfoTooltip';
import CopySenderEmailFlow from './CopySenderEmailFlow';
import ChangeMarketplaceFlow from './ChangeMarketplaceFlow';
import RecipientCountry from './Fields/RecipientCountry';
import RecipientAddress from './Fields/RecipientAddress';
import RecipientApartment from './Fields/RecipientApartment';
import RecipientCompany from './Fields/RecipientCompany';
import RecipientStateSelect from './Fields/RecipientStateSelect';
import RecipientStateText from './Fields/RecipientStateText';
import RecipientStateHidden from './Fields/RecipientStateHidden';
import RecipientCity from './Fields/RecipientCity';
import RecipientZipcode from './Fields/RecipientZipcode';
import RecipientPhone from './Fields/RecipientPhone';
import AddressAutocomplete from './Fields/AddressAutocomplete';

export interface IShippingAddressFormProps {
  onSubmit: (address: IShippingAddress) => void;
  onResetErrors: (fieldName: string) => void;
  onChangeProduct: (countryId: number) => void;
}

const ShippingAddressForm = ({ onSubmit, onResetErrors, onChangeProduct }: IShippingAddressFormProps) => {
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const address = useSelector(getInitialShippingValues);
  const isLoading = useSelector(getIsPending);
  const errors = useSelector(getErrors);
  const sender = useSelector(getSenderState);
  const countryId = useSelector(getCountryId);
  const customStyling = useSelector(getBranding);
  const initialCountry = useSelector(
    useMemo(() => getCountryById(countryId || CommonData.COUNTRIES.US.id), [countryId]),
  );
  const product = useSelector(getProduct);
  const canBeExchanged = useSelector(getCanBeExchanged);
  const isManualAddressMode = useSelector(getIsManualAddressMode);
  const hasShippingAddress = useSelector(getHasShippingAddress);

  const { errors: validationErrors, reset, handleSubmit, control, getValues, watch, setError } = useForm<
    IShippingFormFields
  >({
    mode: 'all',
    defaultValues: getAddressDefaultValues(initialCountry),
    resolver: yupResolver(addressValidationSchema),
  });

  useExternalErrors<IShippingFormFields, Partial<Record<keyof IShippingFormFields, string | string[]>>>(
    setError,
    errors,
  );

  const handleResetError = useCallback(event => onResetErrors(event.target.name), [onResetErrors]);
  const handleEnterAddressManually = useCallback(() => {
    dispatch(setManualAddressMode(true));
    trackEvent('Autocomplete - skipped');
  }, [dispatch, trackEvent]);

  const country = watch(ShippingAddressField.Country);
  const countryStates = useMemo(() => country.states || [], [country]);
  const isProductAvailableForCountry = country.id === product?.countryId;

  const isFieldOptional = useMemo(() => isAddressFieldOptional(country), [country]);
  const isStateOptional = countryStates.length === 0 || isFieldOptional(CountryAddressField.State);
  const isZipCodeOptional = isFieldOptional(CountryAddressField.PostalCode);
  const isPhoneOptional = isFieldOptional(CountryAddressField.Phone);
  const isCityOptional = isFieldOptional(CountryAddressField.City);

  const isContinueButtonDisabled = !isManualAddressMode && !hasShippingAddress;
  const isFormFieldsVisible = (isManualAddressMode || hasShippingAddress) && isProductAvailableForCountry;
  const isAutocompleteVisible = !isManualAddressMode;

  const handleSetShippingAddress = useCallback(
    (data: IShippingFormFields) => {
      onSubmit({
        ...data,
        [ShippingAddressField.Country]: data[ShippingAddressField.Country].id,
      });
    },
    [onSubmit],
  );

  const handleSelectAddress = useCallback(
    (option: IVerifiedAddressOption) => {
      dispatch(setManualAddressMode(true));
      reset({
        [ShippingAddressField.Company]: address.company || '',
        [ShippingAddressField.Address]: option.deliveryAddressLines.join(', '),
        [ShippingAddressField.Address2]: address.address2 || '',
        [ShippingAddressField.City]: option.city,
        [ShippingAddressField.State]: option.stateName,
        [ShippingAddressField.Zipcode]: option.zipcode,
        [ShippingAddressField.Country]: country,
        [ShippingAddressField.Phone]: address.phone || '',
      });
    },
    [dispatch, country, address, reset],
  );

  useEffect(() => {
    reset({
      ...address,
      [ShippingAddressField.Country]: initialCountry,
    });
  }, [reset, address, initialCountry]);

  return (
    <form onSubmit={handleSubmit(handleSetShippingAddress)}>
      <Box mt={2}>
        <RecipientCountry control={control} disabled={isLoading} />
      </Box>
      <Collapse in={isProductAvailableForCountry}>
        <Box mt={2}>
          {isAutocompleteVisible && (
            <AddressAutocomplete
              control={control}
              watch={watch}
              error={validationErrors[ShippingAddressField.Address]?.message}
              disabled={isLoading}
              onSelectAddress={handleSelectAddress}
            />
          )}
          {!isFormFieldsVisible && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
              <LinkButton onClick={handleEnterAddressManually}>Enter address manually</LinkButton>
            </Box>
          )}
          {!isAutocompleteVisible && (
            <RecipientAddress
              control={control}
              label={`${country.labels.address} *`}
              placeholder={country.labels.address}
              error={validationErrors[ShippingAddressField.Address]?.message}
              disabled={isLoading}
              onFocus={handleResetError}
            />
          )}
        </Box>
      </Collapse>
      <Collapse in={isFormFieldsVisible}>
        <Box mt={2} position="relative">
          <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
            <Grid item xs={12} lg={6}>
              <RecipientApartment
                control={control}
                label={country.labels.address2}
                placeholder={country.labels.address2}
                error={validationErrors[ShippingAddressField.Address2]?.message}
                disabled={isLoading}
                onFocus={handleResetError}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box position="relative" display="flex" alignItems="center">
                <RecipientCompany
                  control={control}
                  error={validationErrors[ShippingAddressField.Company]?.message}
                  disabled={isLoading}
                  onFocus={handleResetError}
                />
                <Box ml={1}>
                  <InfoTooltip
                    title="Please include the company name if your gift is been shipped to a business address."
                    dataTestId="ShippingAddressForm.CompanyName.Hint"
                  />
                </Box>
              </Box>
              <Box mt={1} display={['auto', 'none']}>
                <Typography className="Subcopy-Static-Alt">Required if shipped to business address</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box mt={2} data-testid="shippingStateSelect">
          {isStateOptional ? (
            <RecipientStateText
              control={control}
              label={country.labels.state}
              placeholder={country.labels.state}
              disabled={isLoading}
              error={validationErrors[ShippingAddressField.State]?.message}
              onFocus={handleResetError}
            />
          ) : (
            <RecipientStateSelect
              control={control}
              label={country.labels.state}
              states={countryStates}
              disabled={isLoading}
              error={validationErrors[ShippingAddressField.State]?.message}
              onResetErrors={handleResetError}
            />
          )}
          <RecipientStateHidden
            control={control}
            reset={reset}
            getValues={getValues}
            onFocus={handleResetError}
            states={countryStates}
          />
        </Box>
        <Box mt={2}>
          <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
            <Grid item xs={12} sm={12} lg={6} data-testid="ShippingAddressForm.RecipientCity.Grid">
              <RecipientCity
                control={control}
                label={isCityOptional ? country.labels.city : `${country.labels.city} *`}
                placeholder={country.labels.city}
                disabled={isLoading}
                error={validationErrors[ShippingAddressField.City]?.message}
                onFocus={handleResetError}
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={6}>
              <RecipientZipcode
                control={control}
                label={isZipCodeOptional ? country.labels.postalCode : `${country.labels.postalCode} *`}
                placeholder={country.labels.postalCode}
                disabled={isLoading}
                error={validationErrors[ShippingAddressField.Zipcode]?.message}
                onFocus={handleResetError}
              />
            </Grid>
          </Grid>
        </Box>
        <Box mt={2} position="relative" display="flex" alignItems="center">
          <RecipientPhone
            control={control}
            label={isPhoneOptional ? country.labels.phone : `${country.labels.phone} *`}
            placeholder={country.labels.phone}
            disabled={isLoading}
            error={validationErrors[ShippingAddressField.Phone]?.message}
            onFocus={handleResetError}
          />
          <Box ml={1}>
            <InfoTooltip
              title="Phone number will be only used for verifying shipping information."
              dataTestId="ShippingAddressForm.PhoneNumber.Hint"
            />
          </Box>
        </Box>
        <Box mt={1} display={['auto', 'none']}>
          <Typography className="Subcopy-Static-Alt">Only used for shipping verification.</Typography>
        </Box>
        <Box pt={2} color="#9ea9c6" fontFamily="Lato" textAlign="center">
          {customStyling[BrandingFields.disclaimer]} To learn more, please see our{' '}
          <Link
            style={{ display: 'inline' }}
            href={customStyling[BrandingFields.privacyPolicyLink] || 'https://www.alyce.com/privacy-policy'}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>
        </Box>
      </Collapse>
      <Box mt={3} mb={3} width={1}>
        {isProductAvailableForCountry && (
          <StyledSubmitButton
            type="submit"
            data-testid="ShippingAddressForm.SubmitShippingAddress.Button"
            width="100%"
            disabled={isContinueButtonDisabled}
          >
            Continue
          </StyledSubmitButton>
        )}
        {!isProductAvailableForCountry && canBeExchanged && (
          <ChangeMarketplaceFlow
            productName={product?.name || ''}
            country={country}
            onChangeProduct={onChangeProduct}
          />
        )}
        {!isProductAvailableForCountry && !canBeExchanged && sender && (
          <CopySenderEmailFlow
            productName={product?.name || ''}
            countryName={country.name}
            senderEmail={sender.email}
          />
        )}
      </Box>
    </form>
  );
};

export default memo(ShippingAddressForm);
